import { useEffect, useMemo, useState } from 'react';

import { PaymentInfo } from '../../../interfaces/paymentResult';
import { paymentResultService } from '../../../services/paymentResultService';
import { PaymentResultQueryParamsKeys } from '../../../constants/pathQueryParams';
import { getPaymentResultParams } from '../../../utils/getPaymentResultParams';
import { SignUpPaymentResultType } from '../../../constants/signUpPaymentResultType';
import { getIsNonPaidSubscription } from '../../../utils/getIsNonPaidSubscription/getIsNonPaidSubscription';

type UsePaymentResultReturn = {
  paymentInfo: PaymentInfo | null;
  params: Record<PaymentResultQueryParamsKeys, any>;
  paymentResultType: SignUpPaymentResultType;
  isSuccess: boolean;
  isNonPaidSubscription: boolean;
  challengeId?: number;
};

export const useSignUpPaymentResult = (): UsePaymentResultReturn => {
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);

  const params = getPaymentResultParams();
  const {
    paymentIntentId,
    connectedAccountId,
    subscriptionType,
    paymentTimeEpoch,
    senderName,
    paymentResultType,
    redirectStatus,
    challengeId,
  } = params;

  const isSuccess = redirectStatus === 'succeeded';

  const isNonPaidSubscription = useMemo(
    () => getIsNonPaidSubscription(subscriptionType),
    [subscriptionType],
  );

  const getPaymentInfo = async () => {
    if (!paymentIntentId || !connectedAccountId) {
      console.error('Not all required data is set');
    } else {
      const result = await paymentResultService.getPaymentDetails(
        paymentIntentId,
        connectedAccountId,
      );

      setPaymentInfo(result);
    }
  };

  const getPaymentInfoForNonPaidSubscription = async () => {
    if (!paymentTimeEpoch || !senderName) {
      console.error('Not all required data is set');
    } else {
      setPaymentInfo({
        paymentTimeEpoch,
        senderName,
        amount: 0,
        paymentMethod: null,
        cardFunding: null,
        currency: '',
        amountExcludingTax: 0,
      });
    }
  };

  const processPaymentDetails = () => {
    if (isNonPaidSubscription) {
      getPaymentInfoForNonPaidSubscription();
    } else {
      getPaymentInfo();
    }
  };

  useEffect(() => {
    processPaymentDetails();
  }, [isNonPaidSubscription]);

  return {
    paymentInfo,
    params,
    paymentResultType,
    isSuccess,
    isNonPaidSubscription,
    challengeId,
  };
};
